/**
 * Currency Formatter Utility
 * Provides methods for formatting currency values based on locale and currency code
 */

export class CurrencyFormatter {
  /**
   * Format a price value according to the specified currency and culture
   * @param amount - The numeric amount to format
   * @param currencyCode - The ISO 4217 currency code (e.g., "CZK", "EUR", "USD")
   * @param culture - The BCP 47 language tag for locale formatting (e.g., "cs-CZ", "de-DE")
   * @returns Formatted currency string
   */
  static formatPrice(amount: number, currencyCode: string, culture: string): string {
    try {
      const formatter = new Intl.NumberFormat(culture, {
        style: 'currency',
        currency: currencyCode,
      });
      return formatter.format(amount);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error formatting currency:', error);
      // Fallback to simple format
      return `${amount.toFixed(2)} ${currencyCode}`;
    }
  }

  /**
   * Create a reusable formatter for a specific currency and culture
   * Useful when formatting multiple values with the same currency settings
   * @param currencyCode - The ISO 4217 currency code
   * @param culture - The BCP 47 language tag for locale formatting
   * @returns A function that formats amounts with the specified settings
   */
  static createFormatter(currencyCode: string, culture: string): (amount: number) => string {
    const formatter = new Intl.NumberFormat(culture, {
      style: 'currency',
      currency: currencyCode,
    });

    return (amount: number) => formatter.format(amount);
  }
}
